<template>
  <div>
    <StackedHorizontalBarChart
      :title="chartTitle"
      :filterContext="filterContext"
      :dataProviders="dataProviders"
      :contextUpdaters="contextUpdaters"
      :navigationConfig="navigationConfig"
      :chartFontColor="chartFontColor"
      :labelWidth="160"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';
import { executeAndProcessQuery } from '@/utils/stackedBarData';
import { sqlLiteral } from '@/utils/assetFields';

const store = useStore();
const { t } = useI18n();
const filterContext = computed(() => store.state.filterContext);
const chartFontColor = computed(() => store.getters.chartFontColor);
const conn = store.getters.getDBConnection;

const chartName = 'assetCategoryChart';
const chartTitle = t('categoryBreakdown');

// Category -> System -> Type -> Asset ID, every level counting unique assets
// stacked by current status. The asset level is the leaf: clicking a bar
// filters to that one asset instead of drilling further.
const groupedLevel = (table, { group, parent = null }) => `
  SELECT
    a.${group} AS id,
    a.${group} AS title,
    ${parent ? `a.${parent} AS parentId,` : ''}
    s.Title AS status_title,
    s.Color AS color,
    COUNT(*) AS value,
    MIN(s.Sort) AS sort
  FROM ${table} a
  JOIN status_templates s ON a.assetStatusId = s.Id
  WHERE a.${group} IS NOT NULL
  GROUP BY a.${group}${parent ? `, a.${parent}` : ''}, s.Title, s.Color
  ORDER BY a.${group}, sort, status_title
`;

const assetLevel = (table) => `
  SELECT
    a.assetKey AS id,
    COALESCE(a.assetRefId, a.assetKey) AS title,
    a.assetType AS parentId,
    s.Title AS status_title,
    s.Color AS color,
    COUNT(*) AS value,
    MIN(s.Sort) AS sort
  FROM ${table} a
  JOIN status_templates s ON a.assetStatusId = s.Id
  GROUP BY a.assetKey, a.assetRefId, a.assetType, s.Title, s.Color
  ORDER BY title
`;

const table = (filtered) => (filtered ? 'filteredAssets' : 'unique_assets');

const dataProviders = {
  category: (filtered = true) => executeAndProcessQuery(conn, groupedLevel(table(filtered), { group: 'assetCategory' })),
  system: (filtered = true) => executeAndProcessQuery(conn, groupedLevel(table(filtered), { group: 'assetSystem', parent: 'assetCategory' })),
  type: (filtered = true) => executeAndProcessQuery(conn, groupedLevel(table(filtered), { group: 'assetType', parent: 'assetSystem' })),
  asset: (filtered = true) => executeAndProcessQuery(conn, assetLevel(table(filtered))),
};

const applyFilter = (condition, label) =>
  filterContext.value.updateFilterWithCondition(chartName, condition, label);

const contextUpdaters = {
  category: (category) => applyFilter(`assetCategory = ${sqlLiteral(category)}`, `${t('category')}: ${category}`),
  system: (system, category) => applyFilter(
    [`assetSystem = ${sqlLiteral(system)}`, category ? `assetCategory = ${sqlLiteral(category)}` : null]
      .filter(Boolean).join(' AND '),
    `${t('assetSystem')}: ${system}`
  ),
  type: (type, system) => applyFilter(
    [`assetType = ${sqlLiteral(type)}`, system ? `assetSystem = ${sqlLiteral(system)}` : null]
      .filter(Boolean).join(' AND '),
    `${t('assetType')}: ${type}`
  ),
  asset: (assetKey) => applyFilter(`assetKey = ${sqlLiteral(assetKey)}`, `${t('assetId')}: ${assetKey}`),
  reset: async () => { await filterContext.value.removeFilter(chartName); },
};

const navigationConfig = [
  { level: 'category', titleKey: 'category' },
  { level: 'system', titleKey: 'assetSystem' },
  { level: 'type', titleKey: 'assetType' },
  { level: 'asset', titleKey: 'assetId' },
];
</script>
